/**
 * TabChartEventManager - Multi-tab adapted version of ChartEventManager
 *
 * Manages events for individual chart tabs with proper isolation
 * and chart ID-based event filtering.
 *
 * Uses StateViewportManager for multi-state rendering:
 * - Each snapshot gets its own SVG layer
 * - State switching = CSS visibility toggle (instant!)
 * - LRU eviction of old states
 */

import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { EventStream } from '../core/EventStream';
import { TabChartRenderer } from './TabChartRenderer';
import { ApiMapper } from '../../../../../utils/ApiMapper';
import { ReadyChartData } from '../../../../../types/ReadyChartData';
import { JatakaApiResponse } from '../../../../../types/JatakaApiTypes';
import { StateViewportManager } from '../state/StateViewportManager';
import { chakraDataService } from '../../../../../services/ChakraDataService';

/**
 * Tab-aware event manager for chart instances
 */
export class TabChartEventManager {
    private renderer: TabChartRenderer;
    private eventStream: EventStream;
    private debugMode: boolean;
    private chartId: string;
    private subscriptions: Map<string, any> = new Map();
    private stateManager: StateViewportManager;

    constructor(
        renderer: TabChartRenderer,
        eventStream: EventStream,
        debugMode: boolean = false,
        chartId: string
    ) {
        this.renderer = renderer;
        this.eventStream = eventStream;
        this.debugMode = debugMode;
        this.chartId = chartId;
        this.stateManager = new StateViewportManager(chartId);

        this.setupEventHandlers();

        if (debugMode) {
            devLog(`🎯 TabChartEventManager[${chartId}]: Initialized for chart tab`);
        }
    }
    
    /**
     * Setup event handlers with chart ID filtering
     */
    private setupEventHandlers(): void {
        // Chart data update events (strictly filtered by chart ID)
        const dataUpdateSub = this.eventStream.subscribe(`chart.data.updated.${this.chartId}`, async (event: any) => {
            
            await this.handleChartDataUpdate(event);
        });
        this.subscriptions.set('chart.data.updated', dataUpdateSub);
        
        // DOM ready events (chart-specific)
        const domReadySub = this.eventStream.subscribe(`dom.ready.${this.chartId}`, (event: any) => {
            if (this.debugMode) {
            }
            this.handleDOMReady(event);
        });
        this.subscriptions.set('dom.ready', domReadySub);
        
        // Container ready events (filtered by chart ID)
        const containerReadySub = this.eventStream.subscribe('containers.ready', (event: any) => {
            if (event.chartId === this.chartId || !event.chartId) {
                if (this.debugMode) {
                }
                this.handleContainersReady(event);
            }
        });
        this.subscriptions.set('containers.ready', containerReadySub);
        
        // Chart-specific zoom events
        const zoomSub = this.eventStream.subscribe(`ZOOM_CHANGED_${this.chartId}`, (event: any) => {
            if (this.debugMode) {
            }
            this.handleZoomChange(event);
        });
        this.subscriptions.set(`ZOOM_CHANGED_${this.chartId}`, zoomSub);
        
        // Display mode changes (chart-specific)
        const displayModeSub = this.eventStream.subscribe(`DISPLAY_MODE_CHANGED_${this.chartId}`, (event: any) => {
            if (this.debugMode) {
            }
            this.handleDisplayModeChange(event);
        });
        this.subscriptions.set(`DISPLAY_MODE_CHANGED_${this.chartId}`, displayModeSub);

        // Chakra toggle events - clear all states (chakra changes affect rendering)
        const chakraToggleSub = this.eventStream.subscribe('chakra.universal.toggle', (event: any) => {
            if (event.chartId === this.chartId) {
                devLog(`[TabChartEventManager] 🔄 Chakra toggled, clearing states for ${this.chartId}`);
                this.stateManager.clearAll();
            }
        });
        this.subscriptions.set('chakra.universal.toggle', chakraToggleSub);

        // Settings change events - clear states and chakra cache (settings affect rendering)
        const settingsChangeSub = this.eventStream.subscribe(`chart.settings.changed.${this.chartId}`, () => {
            devLog(`[TabChartEventManager] ⚙️ Settings changed, clearing states for ${this.chartId}`);
            this.stateManager.clearAll();
            // Clear chakra data cache - chakras must rebuild with new snapshot data
            chakraDataService.clearCache(this.chartId);
        });
        this.subscriptions.set('chart.settings.changed', settingsChangeSub);
    }
    
    /**
     * Handle chart data update events
     * Uses StateViewportManager for state caching:
     * - If state already cached → instant switch (restore innerHTML)
     * - If new state → render normally → save to cache
     */
    private async handleChartDataUpdate(event: any): Promise<void> {
        try {
            const snapshotId = event.snapshotId;
            devLog(`[TabChartEventManager] 📥 handleChartDataUpdate for ${this.chartId}, source: ${event.source}, snapshotId: ${snapshotId || 'none'}`);

            // If snapshot ID provided and already cached, just restore innerHTML
            if (snapshotId && this.stateManager.isStateRendered(snapshotId)) {
                this.stateManager.switchToState(snapshotId);
                devLog(`[TabChartEventManager] ⚡ Instant switch to state ${snapshotId}`);

                // Emit lines.ready to re-attach planet drag handlers to restored DOM
                this.eventStream.emit({
                    id: `lines-ready-${this.chartId}-${Date.now()}`,
                    type: 'lines.ready',
                    source: `tab-chart-event-manager-${this.chartId}`,
                    timestamp: Date.now(),
                    metadata: {
                        chartId: this.chartId,
                        fromCache: true
                    }
                });

                // Emit chart rendered event (from cache)
                this.eventStream.emit({
                    id: `chart-rendered-${this.chartId}-${Date.now()}`,
                    type: 'chart.rendered',
                    source: `tab-chart-event-manager-${this.chartId}`,
                    chartId: this.chartId,
                    timestamp: Date.now(),
                    metadata: {
                        fromCache: true,
                        snapshotId
                    }
                });
                return;
            }

            // Extract API response from event
            const apiResponse: JatakaApiResponse = event.apiResponse || event.chartData?.apiResponse;

            if (!apiResponse) {
                devWarn(`⚠️ TabChartEventManager[${this.chartId}]: No API response in event`);
                return;
            }

            devLog(`[TabChartEventManager] 📊 API response time: ${apiResponse.basic_info?.time}`);

            // Simple mapping - no validation needed (server guarantees correctness)
            const readyChartData: ReadyChartData = ApiMapper.toReadyChartData(
                apiResponse,
                this.chartId,
                event.source || 'api-event'
            );

            devLog(`[TabChartEventManager] 🎯 Mapped ${readyChartData.planets.length} planets, lagna rasi: ${readyChartData.lagna?.rasi}`);

            // Render chart normally
            const result = await this.renderer.renderChart(readyChartData);
            devLog(`[TabChartEventManager] ✅ renderChart result: success=${result.success}, elements=${result.elementsRendered}`);

            if (result.success) {
                // Save rendered state to cache
                const stateId = snapshotId || `state-${Date.now()}`;
                this.stateManager.saveState(stateId);

                if (this.debugMode) {
                    devLog(`✅ TabChartEventManager[${this.chartId}]: State ${stateId} rendered and cached`);
                }

                // Emit chart rendered event
                this.eventStream.emit({
                    id: `chart-rendered-${this.chartId}-${Date.now()}`,
                    type: 'chart.rendered',
                    source: `tab-chart-event-manager-${this.chartId}`,
                    chartId: this.chartId,
                    timestamp: Date.now(),
                    metadata: {
                        elementsRendered: result.elementsRendered,
                        renderTime: result.renderTime,
                        displayMode: readyChartData.metadata.chartType,
                        stateId,
                        fromCache: false
                    }
                });
            } else {
                devError(`❌ TabChartEventManager[${this.chartId}]: Chart rendering failed`);
            }

        } catch (error) {
            devError(`❌ TabChartEventManager[${this.chartId}]: Error handling chart data update:`, error);
        }
    }

    /**
     * Get the state manager for external access (e.g., planet drag)
     */
    getStateManager(): StateViewportManager {
        return this.stateManager;
    }
    
    /**
     * Handle DOM ready events
     */
    private handleDOMReady(event: any): void {
        if (this.debugMode) {
        }
        
        // DOM initialization is handled by the renderer
        // This is mainly for logging and potential future hooks
    }
    
    /**
     * Handle containers ready events
     */
    private handleContainersReady(event: any): void {
        if (this.debugMode) {
        }
        
        // Container initialization is handled by the renderer
        // This is mainly for logging and potential future hooks
    }
    
    /**
     * Handle zoom change events
     */
    private handleZoomChange(event: any): void {
        if (this.debugMode) {
        }
        
        // Zoom handling is managed by the renderer
        // This is mainly for logging and potential future hooks
    }
    
    /**
     * Handle display mode change events
     */
    private handleDisplayModeChange(event: any): void {
        if (this.debugMode) {
        }
        
        this.renderer.switchDisplayMode(event.mode).catch(error => {
            devError(`❌ TabChartEventManager[${this.chartId}]: Failed to switch display mode:`, error);
        });
    }
    
    /**
     * Force render with ReadyChartData (no ApiMapper needed - data already processed)
     */
    async forceRender(readyChartData: ReadyChartData): Promise<void> {
        if (this.debugMode) {
            devLog(`🎯 TabChartEventManager[${this.chartId}]: Force render requested`);
        }

        const result = await this.renderer.renderChart(readyChartData);

        if (!result.success) {
            throw new Error(`Force render failed for chart ${this.chartId}`);
        }

        if (this.debugMode) {
            devLog(`✅ TabChartEventManager[${this.chartId}]: Force render completed (${result.elementsRendered} elements, ${result.renderTime.toFixed(2)}ms)`);
        }
    }
    
    /**
     * Emit chart-specific event
     */
    emitChartEvent(type: string, data: any): void {
        this.eventStream.emit({
            id: `${type}-${this.chartId}-${Date.now()}`,
            type,
            source: `tab-chart-event-manager-${this.chartId}`,
            chartId: this.chartId,
            timestamp: Date.now(),
            ...data
        });
        
        if (this.debugMode) {
        }
    }
    
    /**
     * Get event manager statistics
     */
    getStats() {
        return {
            chartId: this.chartId,
            subscriptionsCount: this.subscriptions.size,
            debugMode: this.debugMode,
            isActive: this.subscriptions.size > 0
        };
    }
    
    /**
     * Cleanup event subscriptions
     */
    cleanup(): void {
        this.subscriptions.forEach((subscription, key) => {
            if (subscription && typeof subscription.unsubscribe === 'function') {
                subscription.unsubscribe();
            }
        });

        this.subscriptions.clear();

        // Destroy state manager (clears all state layers)
        this.stateManager.destroy();

        if (this.debugMode) {
            devLog(`🧹 TabChartEventManager[${this.chartId}]: Cleanup completed`);
        }
    }
}